import React, { useCallback, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CodelistResponse } from 'api/models'
import { PassiveBorderTransportMeansType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import { TRANSPORT_MEANS_TYPES_PREFIX, TRANSPORT_MODE_CODE_PREFIX } from 'shared/ncts/common/classifier-util'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist, { createTypeOfIdentificationsByModeOfTransportFilter } from 'features/declaration/codelist/api/useCodelist'
import useCodelistOptions from 'features/declaration/codelist/components/useCodelistOptions'
import { blankPassiveBorderTransportMeans } from 'features/declaration/models/blankItems'
import E1103Validator from 'features/declaration/form/validations/E1103'
import FormCollapsibleColumn from '../../../../components/p5/FormCollapsibleColumn'
import FormFieldArray from '../../../../components/p5/FormFieldArray'
import FormField from '../../../../components/p5/FormField'
import FormSelect from '../../../../components/p5/FormSelect'
import Input from '../../../../components/p5/Input'
import useDeclarationFieldRequirements from '../../form/components/useDeclarationFieldRequirements'

const scope = 'passiveBorderTransportMeans'

function PassiveBorderTransportMeans({
  borderModeOfTransport,
  entriesGrid = false,
}: Readonly<{ borderModeOfTransport: string; entriesGrid?: boolean }>) {
  const { t } = useTranslation()

  const { control, setValue } = useFormContext<HouseConsignmentFormValues>()

  const {
    softRemove,
    appendWithSequence,
    visibleFields,
  } = useSoftDeleteFieldArray<HouseConsignmentFormValues, PassiveBorderTransportMeansType>({
    control,
    name: 'passiveBorderTransportMeans',
  })

  const [, countriesOptions] = useCodelist('COUNTRIES')
  const typeOfMeansOfTransportFilterFn = useCallback(
    () => (
      item: CodelistResponse,
    ): boolean => item?.code?.startsWith(`${TRANSPORT_MEANS_TYPES_PREFIX}${
      borderModeOfTransport?.substring(TRANSPORT_MODE_CODE_PREFIX.length)}`) ?? false,
    [borderModeOfTransport],
  )
  const [, typeOfMeansOfTransportOptions] = useCodelist('TRANSPORT_MEANS_TYPES', typeOfMeansOfTransportFilterFn)

  const transportIdentificationsFilterPredicate = useMemo(
    () => createTypeOfIdentificationsByModeOfTransportFilter(borderModeOfTransport),
    [borderModeOfTransport],
  )
  const {
    options: transportIdentificationsOptions,
    isSuccess: isTransportIdentificationsLoaded,
  } = useCodelistOptions('TRANSPORT_IDENTIFICATIONS', transportIdentificationsFilterPredicate)

  useEffect(() => {
    if (!isTransportIdentificationsLoaded) {
      return
    }

    visibleFields.forEach((fieldWithIndex) => {
      const item = fieldWithIndex.field
      const hasCorrectSubType = transportIdentificationsOptions.find((option) => option.value === item.typeOfIdentification)
      const innerItemScope = `${scope}.${fieldWithIndex.index}` as const
      if (!borderModeOfTransport || !hasCorrectSubType) {
        setValue(`${innerItemScope}.typeOfIdentification`, '', {
          shouldDirty: true,
          shouldTouch: true,
        })
      }
    })
  }, [visibleFields, setValue, borderModeOfTransport, isTransportIdentificationsLoaded, transportIdentificationsOptions])

  const createDefault = useCallback(() => blankPassiveBorderTransportMeans, [])

  const paths = useMemo(() => [
    scope,
    ...visibleFields.map((f) => `${scope}.${f.index}.typeOfIdentification`),
    ...visibleFields.map((f) => `${scope}.${f.index}.identificationNumber`),
    ...visibleFields.map((f) => `${scope}.${f.index}.nationality`),
    ...visibleFields.map((f) => `${scope}.${f.index}.typeOfMeansOfTransport`),
  ], [visibleFields])

  const fieldRequirements = useDeclarationFieldRequirements<HouseConsignmentFormValues>('houseConsignment', paths)
  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-deprture-transport"
        headingLabel={t('declaration.p5.borderTransportMeans', { context: 'passive' })}
        fieldRule={fieldRequirements[scope]}
      >
        <div className={entriesGrid ? 'transport-entries-grid' : undefined}>
          {visibleFields.map((fieldWithIndex) => {
            const borderTransportMeans = fieldWithIndex.field
            const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

            return (
              <FormFieldArray
                key={`borderTransportMeans_${borderTransportMeans.id}`}
                titleKey="declaration.p5.sequenceNumber"
                removeItem={() => softRemove(fieldWithIndex.index)}
                sequenceNumber={borderTransportMeans.sequenceNumber + 1}
                bodyClassName="field-array-row"
              >
                <FormField
                  labelKey="declaration.p5.typeOfIdentification"
                  fieldRule={fieldRequirements[`${innerItemScope}.typeOfIdentification`]}
                >
                  <FormSelect
                    <HouseConsignmentFormValues>
                    field={`${innerItemScope}.typeOfIdentification`}
                    labelKey="declaration.p5.typeOfIdentification"
                    options={transportIdentificationsOptions}
                    isDisabled={!borderModeOfTransport}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.typeOfIdentification`]}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.identificationNumber"
                  fieldRule={fieldRequirements[`${innerItemScope}.identificationNumber`]}
                >
                  <Input
                    <HouseConsignmentFormValues>
                    field={`${innerItemScope}.identificationNumber`}
                    labelKey="declaration.p5.identificationNumber"
                    type="text"
                    maxLength={E1103Validator.getIdentificationNumberMaxLength()}
                    placeholder={t('declaration.p5.identificationNumber')}
                    fieldRule={fieldRequirements[`${innerItemScope}.identificationNumber`]}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.nationality"
                  fieldRule={fieldRequirements[`${innerItemScope}.nationality`]}
                >
                  <FormSelect
                    <HouseConsignmentFormValues>
                    field={`${innerItemScope}.nationality`}
                    labelKey="declaration.p5.nationality"
                    options={countriesOptions}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.nationality`]}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.typeOfMeansOfTransport"
                  fieldRule={fieldRequirements[`${innerItemScope}.typeOfMeansOfTransport`]}
                >
                  <FormSelect
                    <HouseConsignmentFormValues>
                    field={`${innerItemScope}.typeOfMeansOfTransport`}
                    labelKey="declaration.p5.typeOfMeansOfTransport"
                    options={typeOfMeansOfTransportOptions}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.typeOfMeansOfTransport`]}
                  />
                </FormField>
              </FormFieldArray>
            )
          })}
        </div>
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              disabled={isAddForbidden}
              onClick={() => appendWithSequence(createDefault())}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewTransportMeans')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default PassiveBorderTransportMeans
